import React, { Component, Fragment } from 'react';
import { BytesSizeUnit, FileUploaderStatus, ThemeColor } from '@philips-internal/oswald-ui-constants';
import { bool, expression, func, number, object, oneOf, record, shape, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, convertBytesSize, enumValues, formatBytes, isFunction } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as AsteriskIcon } from '../../../resources/assets/components/DLS_Asterisk.svg';
import { ReactComponent as CancelIcon } from '../../../resources/assets/components/DLS_CloseCrossCircle.svg';
import { ReactComponent as DeleteIcon } from '../../../resources/assets/components/DLS_DeleteTrash.svg';
import { ReactComponent as FileIcon } from '../../../resources/assets/components/DLS_Document.svg';
import { ReactComponent as PDFIcon } from '../../../resources/assets/components/DLS_DocumentPDF.svg';
import { ReactComponent as ZIPIcon } from '../../../resources/assets/components/DLS_DocumentZip.svg';
import { ReactComponent as Exclamationmark } from '../../../resources/assets/components/DLS_ExclamationMark_Circle.svg';
import { ReactComponent as FileImageIcon } from '../../../resources/assets/components/DLS_FileImage.svg';
import { ReactComponent as RedoIcon } from '../../../resources/assets/components/DLS_Redo.svg';
import { ReactComponent as RoundCheckmark } from '../../../resources/assets/components/DLS_RoundCheckmark.svg';
import { ReactComponent as UploadIcon } from '../../../resources/assets/components/DLS_Upload.svg';
import { Image } from '../image/image.js';
import { Input } from '../input/input.js';
import { Link } from '../link/link.js';
import { Loader } from '../loader/loader.js';
import { ProgressBar } from '../progress-bar/progress-bar.js';
import './file.css';
const maxFileSizeShape = shape({ maxSize: number, sizeUnit: oneOf(['', ...enumValues(BytesSizeUnit)]) });
class File extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            showDescriptionInput: false,
        };
        this.getClassFileName = () => {
            const { status } = this.props;
            return classNames({
                'c-file__name': true,
                'c-file__name-preview': status === FileUploaderStatus.DONE,
            });
        };
        this.getClassNameStatus = () => {
            const { status } = this.props;
            const failCondition = [FileUploaderStatus.ERROR, FileUploaderStatus.INVALID, FileUploaderStatus.MISSING];
            return classNames({
                'c-file__icon-status': true,
                'c-file__icon-status-failed': status ? failCondition.includes(status) : false,
                'c-file__icon-status-load': status === FileUploaderStatus.DONE,
                't-color-signal-green': status === FileUploaderStatus.DONE,
                't-color-signal-orange': status ? failCondition.includes(status) : false,
            });
        };
        this.onClickPredefinedItem = () => {
            const { onClickPredefinedItem, predefinedFile } = this.props;
            if (!isFunction(onClickPredefinedItem) || !predefinedFile) {
                return;
            }
            onClickPredefinedItem(predefinedFile.key);
        };
        this.onPreview = () => {
            const { onPreview, status } = this.props;
            if (status === FileUploaderStatus.DONE && isFunction(onPreview)) {
                onPreview();
            }
        };
        this.onDescriptionClearClick = () => {
            const { description, onChange } = this.props;
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({ oldValue: description, value: '' }));
            }
        };
        /**
         *
         * @returns
         */
        this.renderDescriptionClearAction = () => {
            const { readOnly, description } = this.props;
            if (readOnly || !description) {
                return null;
            }
            return (React.createElement("div", { className: "c-file__description-clear", onClick: this.onDescriptionClearClick, role: "button", tabIndex: 0 },
                React.createElement(CancelIcon, null)));
        };
    }
    componentDidMount() {
        this.buildState();
        this.updateFilePreview();
    }
    componentDidUpdate(prevProps) {
        const { description, path, valid } = this.props;
        if (prevProps.description !== description || prevProps.valid != valid) {
            this.buildState();
        }
        if (path === prevProps.path) {
            return;
        }
        this.updateFilePreview();
    }
    updateFilePreview() {
        const { fileHandlerInstance, type, path } = this.props;
        if (!path || !type || !type.includes('image')) {
            return;
        }
        void fileHandlerInstance.getPreviewURL(path)
            .then((url) => {
            this.setState({ imagePreviewUrl: url });
        });
    }
    buildState() {
        const { description, valid } = this.props;
        if (description || !valid) {
            this.setState({ showDescriptionInput: true });
        }
    }
    renderFileActions() {
        const { onCancel, onClear, onRemove, onRetry, status, readOnly } = this.props;
        const { ABORTED, DONE, ERROR, INVALID, PENDING } = FileUploaderStatus;
        const isStatus = (desiredStatus) => status === desiredStatus;
        if (readOnly) {
            return null;
        }
        return (React.createElement(Fragment, null,
            isStatus(DONE) && (React.createElement(DeleteIcon, { "aria-label": MessageService.translate('tws.file-uploader.delete-image-icon-label'), className: "c-file__icon", onClick: onRemove })),
            (isStatus(ERROR) || isStatus(ABORTED)) && (React.createElement(Fragment, null,
                React.createElement(RedoIcon, { "aria-label": MessageService.translate('tws.file-uploader.retry-upload-icon-label'), className: "c-file__icon", onClick: onRetry }),
                React.createElement(CancelIcon, { "aria-label": MessageService.translate('tws.file-uploader.cancel-upload-icon-label'), className: "c-file__icon", onClick: onCancel }))),
            (isStatus(INVALID)) && (React.createElement(CancelIcon, { "aria-label": MessageService.translate('tws.file-uploader.clear-invalid-icon-label'), className: "c-file__icon", onClick: onClear })),
            isStatus(PENDING) && (React.createElement(CancelIcon, { "aria-label": MessageService.translate('tws.file-uploader.cancel-upload-icon-label'), className: "c-file__icon", onClick: onCancel }))));
    }
    renderStatus() {
        const { status } = this.props;
        const renderStatus = {
            [FileUploaderStatus.ABORTED]: () => null,
            [FileUploaderStatus.DONE]: () => React.createElement(RoundCheckmark, { className: this.getClassNameStatus() }),
            [FileUploaderStatus.ERROR]: () => React.createElement(Exclamationmark, { className: this.getClassNameStatus() }),
            [FileUploaderStatus.INVALID]: () => React.createElement(Exclamationmark, { className: this.getClassNameStatus() }),
            [FileUploaderStatus.LOAD]: () => null,
            [FileUploaderStatus.MISSING]: () => React.createElement(Exclamationmark, { className: this.getClassNameStatus() }),
            [FileUploaderStatus.PENDING]: () => null,
        };
        return status
            ? renderStatus[status]()
            : null;
    }
    renderFileSize() {
        const { size, status } = this.props;
        const hasFileSize = (status === FileUploaderStatus.LOAD || status === FileUploaderStatus.DONE) && size;
        return hasFileSize
            ? React.createElement("span", { className: "c-file__size" }, `(${formatBytes(size)})`)
            : null;
    }
    renderErrorMsg() {
        const { maxFileSize, size, status } = this.props;
        if (status !== FileUploaderStatus.ERROR && status !== FileUploaderStatus.INVALID) {
            return null;
        }
        const convertedSize = maxFileSize && convertBytesSize(size ?? 0, BytesSizeUnit.BYTES, maxFileSize?.sizeUnit);
        let errorMsg = 'tws.file-uploader.error-message';
        if (status === FileUploaderStatus.INVALID) {
            errorMsg = 'tws.file-uploader.invalid';
            if (size && maxFileSize && convertedSize && convertedSize > maxFileSize.maxSize) {
                errorMsg = 'tws.file-uploader.max-file-size';
            }
        }
        const errorMsgOptions = { maxSize: maxFileSize?.maxSize, size: convertedSize, sizeUnit: maxFileSize?.sizeUnit };
        return (React.createElement("span", { className: "t-color-signal-orange c-file__error-msg" }, MessageService.translate(errorMsg, errorMsgOptions)));
    }
    renderLoader() {
        return (React.createElement(Loader, { centered: true, color: ThemeColor.GREY, showMessage: false, size: 24 }));
    }
    getFileTypeIcon(fileType = '') {
        const { status } = this.props;
        const { imagePreviewUrl } = this.state;
        if (status === FileUploaderStatus.PENDING || status === FileUploaderStatus.MISSING) {
            return this.renderLoader();
        }
        if (fileType.includes('image')) {
            return imagePreviewUrl ? React.createElement(Image, { alt: "tws.file-uploader.image-preview", src: imagePreviewUrl }) : React.createElement(FileImageIcon, null);
        }
        if (fileType.includes('pdf')) {
            return React.createElement(PDFIcon, null);
        }
        if (fileType.includes('zip')) {
            return React.createElement(ZIPIcon, null);
        }
        return React.createElement(FileIcon, null);
    }
    renderFileProgress() {
        const { progress = File.defaultProps.progress, size = 0, status } = this.props;
        if (status !== FileUploaderStatus.LOAD && status !== FileUploaderStatus.PENDING) {
            return null;
        }
        const progressBytes = formatBytes((progress / 100) * size);
        const totalBytes = formatBytes(size);
        return (React.createElement("div", { className: "c-file__progress-wrapper" },
            React.createElement(ProgressBar, { percentage: progress }),
            React.createElement("span", { className: "c-file__size" }, `(${progressBytes} / ${totalBytes})`)));
    }
    renderInformation() {
        const { title } = this.props;
        return (React.createElement("div", { className: "c-file__information" },
            React.createElement("span", { className: this.getClassFileName(), onClick: this.onPreview },
                MessageService.translate(title ?? ''),
                " "),
            React.createElement("div", { className: "c-file__status-wrapper" },
                this.renderStatus(),
                this.renderFileProgress(),
                this.renderFileSize(),
                this.renderErrorMsg())));
    }
    renderFile() {
        const { type } = this.props;
        return (React.createElement("li", { className: "c-file" },
            React.createElement("div", { className: "c-file__item" },
                React.createElement("div", { className: "c-file__group u-truncate" },
                    React.createElement("div", { className: "c-file__type-icon" }, this.getFileTypeIcon(type)),
                    this.renderInformation()),
                React.createElement("div", { className: "c-file__actions" }, this.renderFileActions())),
            this.renderDescription()));
    }
    renderPredefined() {
        const { predefinedFile, required, status } = this.props;
        const isMissing = status && status === FileUploaderStatus.MISSING;
        if (!predefinedFile) {
            return null;
        }
        const titleClass = classNames({
            'c-file__predefined-title': true,
            'c-file--no-margin': Boolean(isMissing),
        });
        const predefinedClassName = classNames({
            'c-file__missing-file': Boolean(isMissing),
            'c-file__predefined': true,
        });
        return (React.createElement("div", { className: predefinedClassName, onClick: this.onClickPredefinedItem },
            React.createElement("div", { className: "c-file__content" },
                React.createElement("div", { className: "c-file__content__required-file" },
                    React.createElement("span", { className: titleClass }, MessageService.translate(predefinedFile.value)),
                    required ? React.createElement(AsteriskIcon, { className: "c-file__required-indicator" }) : null),
                React.createElement("div", { className: "c-file__content__required-file-error" },
                    isMissing ? React.createElement(Exclamationmark, { className: this.getClassNameStatus() }) : null,
                    isMissing ? React.createElement("span", { className: "c-file__missing-required-file t-color-signal-red" }, MessageService.translate('tws.file-uploader.required.missing-file')) : null)),
            React.createElement(UploadIcon, { className: "c-file-uploader__icon" })));
    }
    renderDescriptionInput() {
        const { description, descriptionPlaceholderExpression, onBlur, onChange, onFocus, readOnly, valid } = this.props;
        return (React.createElement("div", { className: "c-file__description-input" },
            React.createElement(Input, { enabled: !readOnly, externalAction: this.renderDescriptionClearAction(), onBlur: onBlur, onChange: onChange, onFocus: onFocus, placeholder: descriptionPlaceholderExpression, valid: valid, value: description })));
    }
    renderDescriptionLink() {
        const { addDescriptionExpression, readOnly } = this.props;
        const onDescriptLinkClick = () => this.setState({ showDescriptionInput: true });
        if (readOnly) {
            return null;
        }
        return (React.createElement("div", { className: "c-file__description-link" },
            React.createElement(Link, { onClick: onDescriptLinkClick, value: MessageService.translate(addDescriptionExpression ?? '') })));
    }
    renderDescription() {
        const { canAddDescription } = this.props;
        const { showDescriptionInput } = this.state;
        if (!canAddDescription) {
            return null;
        }
        return (React.createElement("div", { className: "c-file__description" }, showDescriptionInput ? this.renderDescriptionInput() : this.renderDescriptionLink()));
    }
    render() {
        const { progress, status } = this.props;
        const shouldRenderPredefined = status === FileUploaderStatus.PENDING && progress === 0 || status === FileUploaderStatus.MISSING;
        return shouldRenderPredefined
            ? this.renderPredefined()
            : this.renderFile();
    }
}
File.propTypes = {
    addDescriptionExpression: expression,
    canAddDescription: bool,
    description: string,
    descriptionPlaceholderExpression: expression,
    failedUploadExpression: expression,
    fileHandlerInstance: object.notEnumerable(),
    maxFileSize: maxFileSizeShape,
    onBlur: func,
    onCancel: func,
    onChange: func,
    onClear: func,
    onClickPredefinedItem: func,
    onFocus: func,
    onPreview: func,
    onRemove: func,
    onRetry: func,
    path: string,
    predefined: bool,
    /* eslint-disable-next-line sort-keys */
    predefinedFile: record({ key: string, value: string, required: bool }),
    progress: number,
    readOnly: bool,
    required: bool,
    size: number,
    status: oneOf(enumValues(FileUploaderStatus)),
    title: string,
    type: string,
    valid: bool,
};
File.defaultProps = {
    addDescriptionExpression: 'tws.file-uploader.add-description-expression',
    canAddDescription: false,
    descriptionPlaceholderExpression: 'tws.file-uploader.description-placeholder-expression',
    predefined: false,
    progress: 0,
};
export { File, maxFileSizeShape, };
//# sourceMappingURL=file.js.map